import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { delay, finalize } from 'rxjs/operators';
import { DebugDelay } from 'src/app/shared/debug/debug.delay';
import { TypedForm } from 'src/app/shared/forms/typed.form';
import { SortOrder } from 'src/app/shared/types/sort.order';
import { GetPricingSurveysCommand } from 'src/app/surveys/pricing-survey/commands/get.pricing.surveys.command';
import { PricingSurveysService } from 'src/app/surveys/pricing-survey/pricing.surveys.service';
import { PricingSurveysViewModel } from 'src/app/surveys/pricing-survey/viewmodels/pricing.surveys.viewmodel';

@Component({
  selector: 'fit-pricing-surveys',
  templateUrl: './pricing.surveys.component.html',
  styleUrls: ['./pricing.surveys.component.scss']
})
export class PricingSurveysComponent implements OnDestroy {

  busyLoading = false;

  filterForm = new FilterForm();
  sortOrders = new Map<string, SortOrder>();
  page = 1;
  pageSize = 25;
  sortBy = '';
  sortOrder = SortOrder.descending;

  viewmodel: PricingSurveysViewModel;
  destroyed = new Subject();

  constructor(private surveyService: PricingSurveysService,
    private router: Router,
    private title: Title,
    @Inject(DOCUMENT) private document: Document) {
    this.sortOrders.set('InstitutionalOrWholesale', SortOrder.none);
    this.sortOrders.set('FundCode', SortOrder.none);
    this.sortOrders.set('FundName', SortOrder.none);
    this.sortOrders.set('MatchFundCode', SortOrder.none);
    this.sortOrders.set('MatchFundName', SortOrder.none);
    this.sortOrders.set('Status', SortOrder.none);
    this.sortOrders.set('CreatedDate', SortOrder.none);
    this.sortOrders.set('Age', SortOrder.none);
    this.sortOrders.set('ValueAssessment', SortOrder.none);
  }

  get canRefresh() {
    return !this.busyLoading;
  }

  get pageSizeButtonText(): string {
    return `Page size: ${this.pageSize}`;
  }

  ngOnInit() {
    this.title.setTitle("Pricing Surveys");
    this.load();
  }

  load() {
    this.busyLoading = true;

    const command = new GetPricingSurveysCommand (
      this.page,
      this.pageSize,
      this.filterForm.fund.typedValue,
      this.filterForm.matchFund.typedValue,
      this.sortBy,
      this.sortOrder);

    this.surveyService.getPricingSurveysForUser(command)
      .pipe(delay(DebugDelay.value), finalize(() => this.busyLoading = false))
      .subscribe((data) => {
        this.viewmodel = new PricingSurveysViewModel(data);
      });
    
  }

  runSearch() {
    this.page = 1;
    this.load();
  }

  submitSearch(args: KeyboardEvent) {
    if (args.key === "Enter") {
      this.runSearch();
    }
  }

  clearSearch() {
    this.filterForm.reset({ });
    this.runSearch();
  }

  pageChange(pageNumber: number) {
    this.page = pageNumber;
    this.document.body.scrollIntoView(true);
    this.load();
  }

  setPageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.runSearch();
  }

  setSortOrder(args: [string, SortOrder]) {

    this.sortOrders.forEach((v, k, m) => {
      if (k === args[0]) {
        m.set(k, args[1]);
      }
      else {
        m.set(k, SortOrder.none);
      }
    });

    this.sortBy = args[0];
    this.sortOrder = args[1];
    this.page = 1;

    this.load();
  }

  viewSurvey(surveyId: string) {
    this.router.navigate(["surveys/pricing/survey", surveyId]);
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }
}

class FilterForm extends TypedForm {
  fund = this.addTypedControl<string>("fund");
  matchFund = this.addTypedControl<string>("matchFund");
}
